"use client";

import { type ReactNode, useRef } from "react";
import { flushSync } from "react-dom";
import { useLocale, useTranslations } from "next-intl";
import { Button, Input, Label } from "@qrody/ui";
import {
  type AnonymousWifiAuth,
  TEXT_PAYLOAD_MAX_CHARS,
} from "@qrody/api-client/render";
import type { QrMode } from "@qrody/api-client/codes";
import type { GeneratorCapabilities } from "../domain/capabilities";
import { Ticket } from "lucide-react";
import {
  PAYLOAD_TYPE_ICONS,
  PROMO_CODE_PRESETS,
  WIFI_AUTHS,
  isDynamicCompatible,
  promoCodeUrl,
} from "../domain/payload";
import {
  PAYLOAD_TYPES,
  type PayloadType,
  type QrGeneratorFormState,
} from "../domain/seed";
import { Field } from "../rendering/preview-pane";

export interface ContentPanelProps {
  type: PayloadType;
  setType: (type: PayloadType) => void;
  form: QrGeneratorFormState;
  update: <K extends keyof QrGeneratorFormState>(
    key: K,
    value: QrGeneratorFormState[K],
  ) => void;
  ariaInvalid: (id: string) => true | undefined;
  caps: GeneratorCapabilities;
  disableTypeChange: boolean;
  disablePayloadFields: boolean;
  modePreference: QrMode;
  setModePreference: (mode: QrMode) => void;
  hasRendered: boolean;
  appUrl: (path: string) => string;
  persistDraftToStorage: () => void;
}

/**
 * Left column of the generator surface (anchored on lg+ in a flex-column
 * wrapper alongside the optional account-CTA). Holds payload-type tabs,
 * the per-type field renderer, the dynamic-compatibility hint, and the
 * static/dynamic mode toggle (only visible when the caller's caps allow
 * saving).
 */
export function ContentPanel({
  type,
  setType,
  form,
  update,
  ariaInvalid,
  caps,
  disableTypeChange,
  disablePayloadFields,
  modePreference,
  setModePreference,
  hasRendered,
  appUrl,
  persistDraftToStorage,
}: ContentPanelProps) {
  const t = useTranslations("generator");
  const tEditor = useTranslations("editor.fields");
  const tType = useTranslations("editor.type");
  const locale = useLocale();

  return (
    <div className="contents lg:flex lg:flex-col lg:gap-3 lg:col-start-1 lg:row-start-1">
      {/* INHALT — Mobile: 1. Section nach der Top-Bar.
          md: links neben CTA. lg: oben in Col-1-Wrapper. */}
      <section
        aria-labelledby="content-heading"
        className="space-y-3 rounded-xl border bg-card p-3 shadow-sm sm:p-4 order-2 md:order-2"
      >
        <div>
          <h2 id="content-heading" className="text-sm font-semibold">
            {t("section.content")}
          </h2>
          <p className="text-xs text-muted-foreground">
            {t("section.contentHint")}
          </p>
        </div>

        <div className="flex flex-wrap gap-1.5" role="tablist">
          {PAYLOAD_TYPES.map((p) => {
            const Icon = PAYLOAD_TYPE_ICONS[p];
            return (
              <button
                key={p}
                type="button"
                role="tab"
                aria-selected={type === p}
                disabled={disableTypeChange && type !== p}
                className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                  type === p ? "bg-primary text-primary-foreground" : "bg-background"
                }`}
                onClick={() => setType(p)}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden />
                {tType(p)}
              </button>
            );
          })}
        </div>

        <fieldset
          disabled={disablePayloadFields}
          className="space-y-3 border-0 p-0 disabled:opacity-70"
        >
          <PayloadFields
            type={type}
            form={form}
            update={update}
            ariaInvalid={ariaInvalid}
            t={t}
            tEditor={tEditor}
          />
        </fieldset>

        {caps.showAccountCTAs && isDynamicCompatible(type) && (
          <p className="rounded-md border border-dashed border-primary/30 bg-primary/5 px-3 py-2 text-xs text-foreground">
            {t("contextualHint.dynamic")}{" "}
            <a
              href={appUrl(`/${locale}/signup?ref=anon-dynamic-hint`)}
              onClick={persistDraftToStorage}
              className="font-semibold text-primary underline-offset-2 hover:underline"
            >
              {t("contextualHint.cta")}
            </a>
          </p>
        )}

        {caps.primaryAction === "save"
          && isDynamicCompatible(type)
          && !disableTypeChange ? (
          <div className="rounded-md border bg-muted/30 p-3 text-xs">
            <div className="font-medium">Code-Modus</div>
            <div className="mt-1.5 grid gap-1 sm:grid-cols-2">
              <label
                className={`flex cursor-pointer items-start gap-2 rounded-md border p-2 transition-colors ${
                  modePreference === "DYNAMIC" ? "border-primary bg-primary/5" : "hover:bg-muted"
                }`}
              >
                <input
                  type="radio"
                  name="qr-mode"
                  value="DYNAMIC"
                  checked={modePreference === "DYNAMIC"}
                  onChange={() => setModePreference("DYNAMIC")}
                  className="mt-0.5 accent-primary"
                />
                <span>
                  <span className="block font-semibold">Dynamic (empfohlen)</span>
                  <span className="block text-muted-foreground">
                    Slug-Redirect — Ziel-URL bleibt nach dem Druck editierbar.
                  </span>
                </span>
              </label>
              <label
                className={`flex cursor-pointer items-start gap-2 rounded-md border p-2 transition-colors ${
                  modePreference === "STATIC" ? "border-primary bg-primary/5" : "hover:bg-muted"
                }`}
              >
                <input
                  type="radio"
                  name="qr-mode"
                  value="STATIC"
                  checked={modePreference === "STATIC"}
                  onChange={() => setModePreference("STATIC")}
                  className="mt-0.5 accent-primary"
                />
                <span>
                  <span className="block font-semibold">Static</span>
                  <span className="block text-muted-foreground">
                    URL direkt in der Matrix — funktioniert ohne unsere Server, nicht mehr änderbar.
                  </span>
                </span>
              </label>
            </div>
          </div>
        ) : null}
      </section>

      {/* Account-CTA — bei mobile/md über `order-3 md:order-2` visuell
          zwischen QR und Gestaltung (mobile) bzw. neben Inhalt (md). */}
      {hasRendered && caps.showAccountCTAs && (
        <div className="@container order-3 md:order-2">
          <aside
            aria-labelledby="anon-cta-title"
            className="rounded-xl border bg-muted/40 p-4 @md:flex @md:items-center @md:justify-between @md:gap-6 @md:p-5"
          >
            <div className="max-w-prose">
              <h3 id="anon-cta-title" className="text-base font-semibold">
                {t("cta.title")}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">{t("cta.body")}</p>
            </div>
            <Button asChild className="mt-3 shrink-0 @md:mt-0">
              <a
                href={appUrl(`/${locale}/signup?ref=anon-generator-cta`)}
                onClick={persistDraftToStorage}
              >
                {t("cta.button")}
              </a>
            </Button>
          </aside>
        </div>
      )}
    </div>
  );
}

interface PayloadFieldsProps {
  type: PayloadType;
  form: QrGeneratorFormState;
  update: <K extends keyof QrGeneratorFormState>(
    key: K,
    value: QrGeneratorFormState[K],
  ) => void;
  ariaInvalid: (id: string) => true | undefined;
  t: ReturnType<typeof useTranslations>;
  tEditor: ReturnType<typeof useTranslations>;
}

function PayloadFields({
  type,
  form,
  update,
  ariaInvalid,
  t,
  tEditor,
}: PayloadFieldsProps): ReactNode {
  const urlInputRef = useRef<HTMLInputElement>(null);

  // Pre-fill the URL with a store redemption link and park the caret at
  // the end, so the user only has to paste/type the code. flushSync commits
  // the new value before we move the caret; requestAnimationFrame is not
  // reliable here (it does not fire while the window is occluded).
  const applyPromoPreset = (url: string) => {
    flushSync(() => update("url", url));
    const el = urlInputRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  };

  switch (type) {
    case "url":
      return (
        <div className="space-y-2">
          <Label htmlFor="url">{t("field.url")}</Label>
          <Input
            ref={urlInputRef}
            id="url"
            type="url"
            value={form.url}
            onChange={(e) => update("url", e.target.value)}
            autoComplete="off"
            aria-invalid={ariaInvalid("url")}
          />
          <div
            role="group"
            aria-label={t("promoPreset.label")}
            className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground"
          >
            <span>{t("promoPreset.label")}</span>
            {PROMO_CODE_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                data-testid={`promo-preset-${p.id}`}
                className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-0.5 text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => applyPromoPreset(promoCodeUrl(p.id))}
              >
                <Ticket className="h-3 w-3" aria-hidden />
                {t(`promoPreset.${p.id}`)}
              </button>
            ))}
          </div>
        </div>
      );
    case "text":
      return (
        <div className="space-y-2">
          <div className="flex items-baseline justify-between">
            <Label htmlFor="text">{t("field.text")}</Label>
            <span
              aria-live="polite"
              className={`text-xs tabular-nums ${
                form.text.length > TEXT_PAYLOAD_MAX_CHARS * 0.9
                  ? "text-destructive"
                  : "text-muted-foreground"
              }`}
            >
              {form.text.length} / {TEXT_PAYLOAD_MAX_CHARS}
            </span>
          </div>
          <Input
            id="text"
            value={form.text}
            onChange={(e) => update("text", e.target.value)}
            maxLength={TEXT_PAYLOAD_MAX_CHARS}
            autoComplete="off"
            aria-invalid={ariaInvalid("text")}
          />
        </div>
      );
    case "email":
      return (
        <>
          <Field id="emailTo" label={tEditor("emailTo")}>
            <Input
              id="emailTo"
              type="email"
              value={form.emailTo}
              onChange={(e) => update("emailTo", e.target.value)}
              aria-invalid={ariaInvalid("emailTo")}
            />
          </Field>
          <Field id="emailSubject" label={tEditor("emailSubject")}>
            <Input
              id="emailSubject"
              value={form.emailSubject}
              onChange={(e) => update("emailSubject", e.target.value)}
              aria-invalid={ariaInvalid("emailSubject")}
            />
          </Field>
          <Field id="emailBody" label={tEditor("emailBody")}>
            <Input
              id="emailBody"
              value={form.emailBody}
              onChange={(e) => update("emailBody", e.target.value)}
              aria-invalid={ariaInvalid("emailBody")}
            />
          </Field>
        </>
      );
    case "sms":
      return (
        <>
          <Field id="smsTo" label={tEditor("smsTo")}>
            <Input
              id="smsTo"
              type="tel"
              value={form.smsTo}
              onChange={(e) => update("smsTo", e.target.value)}
              aria-invalid={ariaInvalid("smsTo")}
            />
          </Field>
          <Field id="smsMessage" label={tEditor("smsMessage")}>
            <Input
              id="smsMessage"
              value={form.smsMessage}
              onChange={(e) => update("smsMessage", e.target.value)}
              aria-invalid={ariaInvalid("smsMessage")}
            />
          </Field>
        </>
      );
    case "geo":
      return (
        <div className="grid grid-cols-2 gap-3">
          <Field id="geoLat" label={tEditor("latitude")}>
            <Input
              id="geoLat"
              inputMode="decimal"
              value={form.geoLat}
              onChange={(e) => update("geoLat", e.target.value)}
              aria-invalid={ariaInvalid("geoLat")}
            />
          </Field>
          <Field id="geoLon" label={tEditor("longitude")}>
            <Input
              id="geoLon"
              inputMode="decimal"
              value={form.geoLon}
              onChange={(e) => update("geoLon", e.target.value)}
              aria-invalid={ariaInvalid("geoLon")}
            />
          </Field>
        </div>
      );
    case "wifi":
      return (
        <>
          <Field id="wifiSsid" label={tEditor("ssid")}>
            <Input
              id="wifiSsid"
              value={form.wifiSsid}
              onChange={(e) => update("wifiSsid", e.target.value)}
              aria-invalid={ariaInvalid("wifiSsid")}
            />
          </Field>
          <Field id="wifiAuth" label={tEditor("authentication")}>
            <select
              id="wifiAuth"
              className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              value={form.wifiAuth}
              onChange={(e) => update("wifiAuth", e.target.value as AnonymousWifiAuth)}
            >
              {WIFI_AUTHS.map((a) => (
                <option key={a} value={a}>
                  {tEditor(`auth.${a}`)}
                </option>
              ))}
            </select>
          </Field>
          {form.wifiAuth !== "NOPASS" && (
            <Field id="wifiPassword" label={tEditor("password")}>
              <Input
                id="wifiPassword"
                type="text"
                value={form.wifiPassword}
                onChange={(e) => update("wifiPassword", e.target.value)}
                aria-invalid={ariaInvalid("wifiPassword")}
              />
            </Field>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.wifiHidden}
              onChange={(e) => update("wifiHidden", e.target.checked)}
            />
            {tEditor("hidden")}
          </label>
        </>
      );
    case "vcard":
      return (
        <>
          <Field id="vcardFullName" label={tEditor("fullName")}>
            <Input
              id="vcardFullName"
              value={form.vcardFullName}
              onChange={(e) => update("vcardFullName", e.target.value)}
              aria-invalid={ariaInvalid("vcardFullName")}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field id="vcardEmail" label={tEditor("email")}>
              <Input
                id="vcardEmail"
                type="email"
                value={form.vcardEmail}
                onChange={(e) => update("vcardEmail", e.target.value)}
                aria-invalid={ariaInvalid("vcardEmail")}
              />
            </Field>
            <Field id="vcardPhone" label={tEditor("phone")}>
              <Input
                id="vcardPhone"
                type="tel"
                value={form.vcardPhone}
                onChange={(e) => update("vcardPhone", e.target.value)}
                aria-invalid={ariaInvalid("vcardPhone")}
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field id="vcardOrg" label={tEditor("org")}>
              <Input
                id="vcardOrg"
                value={form.vcardOrg}
                onChange={(e) => update("vcardOrg", e.target.value)}
                aria-invalid={ariaInvalid("vcardOrg")}
              />
            </Field>
            <Field id="vcardTitle" label={tEditor("jobTitle")}>
              <Input
                id="vcardTitle"
                value={form.vcardTitle}
                onChange={(e) => update("vcardTitle", e.target.value)}
                aria-invalid={ariaInvalid("vcardTitle")}
              />
            </Field>
          </div>
          <Field id="vcardUrl" label={tEditor("websiteUrl")}>
            <Input
              id="vcardUrl"
              type="url"
              value={form.vcardUrl}
              onChange={(e) => update("vcardUrl", e.target.value)}
              aria-invalid={ariaInvalid("vcardUrl")}
            />
          </Field>
        </>
      );
    case "file":
      return (
        <Field id="fileUrl" label={tEditor("fileUrl")}>
          <Input
            id="fileUrl"
            type="url"
            value={form.fileUrl}
            onChange={(e) => update("fileUrl", e.target.value)}
            aria-invalid={ariaInvalid("fileUrl")}
          />
        </Field>
      );
  }
}
